/* Squigli Christmas ornament builder: one row per name, each with its own design (tap a name, then pick its design),
   live preview, all names to the cart drawer in one go (each name -> its design's variant).
   Each design (section block -> [data-designs] JSON) has its artwork, viewBox in mm and name rules: centre x/y, tilt,
   capital height and longest width. For the tree these match cad/tree_name.py: capitals 18 mm, at most 58 mm wide,
   ink-box centre 50 mm up the tree (y 59 from the top of the 109 mm artwork), tilted 8 degrees, letters thickened 0.15 mm.
   A design with a 3D model (the Christmas ball: block setting "model") shows a live, turning 3D ornament instead
   (squigli-ball3d.js + three.js, loaded only then); its flat artwork stays underneath as the fallback. */
(() => {
  if (customElements.get('squigli-ornament')) return;

  const FONT = 'Squigli Xmas';
  const CAP_RATIO = 828 / 1000;        // the font's capital height, as a share of its size
  const BOLD = 0.15;
  const TREE = {title: 'Tree', variant: 0, available: false, price: 0, art: '', viewbox: '-45 0 90 112', x: 0, y: 59, tilt: 8, cap: 18, maxw: 58, wcm: '8.3', hcm: '10.7'};
  // letters the font has: English + accented Latin, spaces, hyphen, apostrophes, &
  const ALLOWED = /^[A-Za-zÀ-ÖØ-öø-ÿŸ '’&.-]*$/;
  const SVGNS = 'http://www.w3.org/2000/svg';

  // "rAWAN" -> "Rawan", "mary elizabeth" -> "Mary Elizabeth", "jean-luc" -> "Jean-Luc"
  const nameCase = (v) => String(v).toLowerCase().replace(/(^|[\s-])(\S)/g, (m, sep, ch) => {
    const up = ch.toUpperCase();
    return sep + (up.length === 1 ? up : ch);   // skip letters whose capital is two letters (ß)
  });

  let measureCtx;
  const measure = (text) => {
    measureCtx = measureCtx || document.createElement('canvas').getContext('2d');
    measureCtx.font = `100px "${FONT}"`;
    const m = measureCtx.measureText(text);
    return {left: m.actualBoundingBoxLeft, right: m.actualBoundingBoxRight, asc: m.actualBoundingBoxAscent, desc: m.actualBoundingBoxDescent};
  };

  // Draw `name` into the <svg> laid over the design's artwork (same viewBox, in mm).
  const drawName = (svg, name, d) => {
    svg.textContent = '';
    if (!name) return;
    const m = measure(name);
    let size = d.cap / CAP_RATIO;
    const ink = (m.left + m.right) / 100 * size;
    if (ink > d.maxw) size *= d.maxw / ink;
    const k = size / 100;
    const x = -((m.right - m.left) / 2) * k, y = -((m.desc - m.asc) / 2) * k;
    // with a top line (d.top, from the top of the artwork) the name hangs from it; otherwise its ink box is centred on d.y
    const cy = (d.top !== null && d.top !== undefined && d.top !== '') ? Number(d.top) + ((m.asc + m.desc) / 2) * k : d.y;
    const g = document.createElementNS(SVGNS, 'g');
    g.setAttribute('transform', `translate(${d.x} ${cy}) rotate(${-d.tilt})`);
    const layer = (fill, dx, dy, cls) => {
      const t = document.createElementNS(SVGNS, 'text');
      t.textContent = name;
      t.setAttribute('x', (x + dx).toFixed(3)); t.setAttribute('y', (y + dy).toFixed(3));
      t.setAttribute('font-size', size.toFixed(3));
      t.setAttribute('fill', fill); t.setAttribute('stroke', fill);
      t.setAttribute('stroke-width', 2 * (Number(d.bold) || BOLD)); t.setAttribute('stroke-linejoin', 'round');
      if (cls) t.setAttribute('class', cls);
      g.append(t);
    };
    layer('#c9cbc4', 0.28, 0.45, 'orn__name-side');   // raised-letter edge
    layer('#ffffff', 0, 0, 'orn__name-face');
    svg.append(g);
  };

  class SquigliOrnament extends HTMLElement {
    connectedCallback() {
      try { this.i18n = JSON.parse((this.querySelector('[data-i18n]') || {}).textContent || '{}'); } catch (e) { this.i18n = {}; }
      const $ = (s) => this.querySelector(s);
      this.els = {
        rows: $('[data-rows]'), tpl: $('[data-row-tpl]'), more: $('[data-more]'), big: $('[data-name-svg]'), caption: $('[data-caption]'),
        strip: $('[data-strip]'), thumbs: $('[data-thumbs]'), each: $('[data-each]'), total: $('[data-total]'),
        add: $('[data-add]'), pre: $('[data-preorder]'), msg: $('[data-msg]'), treeImg: $('[data-big] img'), sizeText: $('[data-size-text]'),
        designBtns: [...this.querySelectorAll('[data-design]')],
      };
      try { this.designs = JSON.parse((this.querySelector('[data-designs]') || {}).textContent || '[]'); } catch (e) { this.designs = []; }
      if (!this.designs.length) this.designs = [{...TREE, art: this.els.treeImg.src}];
      this.lastDesign = this.designs[Number(this.dataset.start)] ? Number(this.dataset.start) : 0;   // a design's own page starts on it
      this.design = this.designs[this.lastDesign];   // new names start with the design picked last
      this.els.designFor = $('[data-design-for]');
      this.els.designBtns.forEach((b) => b.addEventListener('click', () => this.pickDesign(Number(b.dataset.design))));
      this.addEventListener('keydown', (e) => this.arrowKeys(e));
      this.cur = this.dataset.currency || '$';
      this.max = Number(this.dataset.max) || 20;
      this.examples = String(this.dataset.examples || '').split(',').map((s) => s.trim()).filter(Boolean);
      this.exampleAt = 0;

      this.els.more.addEventListener('click', () => { const r = this.addRow(''); r.querySelector('input').focus(); });
      if (this.els.add) this.els.add.addEventListener('click', () => this.addToCart());
      this.addRow(new URLSearchParams(location.search).get('name') || '');
      this.active = this.rows()[0];

      const ready = document.fonts ? document.fonts.load(`100px "${FONT}"`) : Promise.resolve();
      ready.then(() => this.update()).catch(() => this.update());
      // phones: the pinned tree sits just under the sticky header whenever the header is showing
      const header = document.querySelector('.section-header');
      let queued = false;
      const place = () => {
        queued = false;
        const lights = header?.querySelector('.xmas-lights') ? 12 : 0;    // the Christmas fairy lights hang below the header
        const hb = header ? header.getBoundingClientRect().bottom : 0;
        const garland = document.querySelector('.xmas-garland');          // Christmas pages: snow + lights pinned at the top
        const away = header && (header.classList.contains('shopify-section-header-hidden') || hb <= 0);
        const bottom = away && garland ? garland.offsetHeight : Math.max(0, Math.round(hb) + (hb > 0 ? lights : 0));
        if (bottom !== this.ornTop) { this.ornTop = bottom; this.style.setProperty('--orn-top', `${bottom}px`); }
        // pinned = the preview has reached the top; only then does it cover the strip above it (at the top of the page
        // that cover would sit on the title)
        const stage = this.querySelector('.orn__stage');
        const pinned = Boolean(stage) && stage.getBoundingClientRect().top <= bottom + 1;
        if (pinned !== this.pinned) { this.pinned = pinned; this.classList.toggle('is-pinned', pinned); }
        // the pinned ornament only takes the room that's left, so the name being typed stays in view (keyboard open too)
        // The pinned ornament's height: steady while scrolling (the address bar showing/hiding doesn't make it jump or the
        // 3D ball resize); typing (keyboard up) makes it smaller, leaving room for the mini design row and the typed name.
        let h;
        if (this.typing) {
          const seen = window.visualViewport ? window.visualViewport.height : window.innerHeight;
          h = Math.round(Math.max(84, Math.min(window.innerHeight * 0.3, 260, seen - bottom - 250)));
        } else {
          // locked to the screen size: re-measured only on a new width (rotation) or a big height change, not the address bar
          const vh = window.innerHeight;
          if (window.innerWidth !== this.lockW || Math.abs(vh - this.lockH) > vh * 0.25) { this.lockW = window.innerWidth; this.lockH = vh; }
          h = Math.round(Math.max(130, Math.min(this.lockH * 0.36, 340, this.lockH - 300)));
        }
        if (Math.abs(h - (this.treeH || 0)) > 2) { this.treeH = h; this.style.setProperty('--orn-tree-h', `${h}px`); }
      };
      this.place = place;
      let settle;
      window.addEventListener('scroll', () => {
        if (!queued) { queued = true; requestAnimationFrame(place); }
        clearTimeout(settle); settle = setTimeout(place, 350);   // the header slides back in after scrolling up
      }, {passive: true});
      if (header) ['transitionend', 'animationend'].forEach((ev) => header.addEventListener(ev, place));
      window.addEventListener('resize', place);
      if (window.visualViewport) {   // phone keyboard opening/closing
        let seenH = window.visualViewport.height;
        window.visualViewport.addEventListener('resize', () => {
          const drop = seenH - window.visualViewport.height;
          seenH = window.visualViewport.height;
          place();
          if (this.typing && drop > 120) this.keepInView();   // the keyboard just opened (not the address bar moving while scrolling)
        });
      }
      this.initTyping();
      place();
      // phones: the floating WhatsApp button would sit on the right-hand design card, so it steps aside while they show
      const picker = this.querySelector('.orn__designs');
      if (picker && 'IntersectionObserver' in window) {
        new IntersectionObserver(([e]) => document.documentElement.classList.toggle('orn-picking', e.isIntersecting)).observe(picker);
      }
      this.startExamples();
    }

    t(key, fallback, vars) {
      // Shopify's t filter HTML-escapes ("I'd" arrives as "I&#39;d"); decode, these strings end up as plain text
      const ta = document.createElement('textarea');
      ta.innerHTML = this.i18n[key] || fallback;
      let s = ta.value;
      Object.keys(vars || {}).forEach((k) => { s = s.split(`%${k}%`).join(String(vars[k])); });
      return s;
    }

    money(n) {
      return this.cur + (Number.isInteger(n) ? String(n) : n.toFixed(2));
    }

    rows() { return [...this.els.rows.children]; }

    addRow(value) {
      if (this.rows().length >= this.max) return this.rows().at(-1);
      const row = this.els.tpl.content.firstElementChild.cloneNode(true);
      row.dataset.design = String(this.lastDesign);
      const input = row.querySelector('input');
      const art = row.querySelector('[data-row-art]');
      if (art) art.addEventListener('click', () => input.focus());
      input.value = nameCase(value.slice(0, 16));
      input.addEventListener('input', () => {
        const fixed = nameCase(input.value);
        if (fixed !== input.value) {   // keep the cursor where it was
          const a = input.selectionStart, z = input.selectionEnd;
          input.value = fixed;
          try { input.setSelectionRange(a, z); } catch (e) {}
        }
        this.active = row; this.justAdded = false; this.update();
      });
      input.addEventListener('focus', () => { this.active = row; this.update(); });
      input.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        if (input.value.trim()) { const r = this.addRow(''); r.querySelector('input').focus(); }
      });
      row.querySelector('[data-remove]').addEventListener('click', () => this.removeRow(row));
      this.els.rows.append(row);
      this.active = row;
      this.update();
      return row;
    }

    removeRow(row) {
      const list = this.rows();
      if (list.length === 1) { row.querySelector('input').value = ''; this.active = row; this.update(); return; }
      const i = list.indexOf(row);
      row.remove();
      this.active = this.rows()[Math.max(0, i - 1)];
      this.active.querySelector('input').focus();
      this.update();
    }

    clean(v) { return String(v || '').replace(/\s+/g, ' ').trim(); }

    designOf(row) { return this.designs[Number(row?.dataset.design) || 0] || this.designs[0]; }

    // While the active row is empty, the tree cycles through example names.
    startExamples() {
      clearInterval(this.exampleTimer);
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.exampleTimer = setInterval(() => {
        if (document.hidden || this.examples.length < 2 || this.clean(this.active?.querySelector('input').value)) return;
        this.exampleAt = (this.exampleAt + 1) % this.examples.length;
        this.els.big.classList.add('is-swapping');
        setTimeout(() => { this.drawBig(); this.els.big.classList.remove('is-swapping'); }, 220);
      }, 2600);
    }

    drawBig() {
      const typed = this.active ? this.clean(this.active.querySelector('input').value) : '';
      const ok = ALLOWED.test(typed);
      const shown = typed && ok ? typed : (this.examples[this.exampleAt] || 'Maria');
      const live3d = Boolean(this.api3d && this.design.model);   // the 3D ball covers the flat name: don't draw what's hidden
      if (!live3d || this.flatFor !== this.design) { drawName(this.els.big, shown, this.design); this.flatFor = live3d ? this.design : null; }
      this.shownName = shown;
      if (this.api3d && this.design.model && !this.name3dQueued) {   // the 3D name: once per frame at most
        this.name3dQueued = true;
        requestAnimationFrame(() => { this.name3dQueued = false; if (this.api3d && this.design.model) this.api3d.setName(this.shownName); });
      }
      this.els.big.classList.toggle('is-example', !(typed && ok));
      const alt = this.t('preview_alt', 'Christmas tree ornament with the name %name%', {name: shown});
      this.els.caption.textContent = alt;
      this.els.treeImg.alt = alt;
    }

    // Live 3D preview for designs that have a model; loads the first time one is shown. No WebGL -> flat artwork stays.
    sync3d(d) {
      const fig = this.els.treeImg.closest('[data-big]');
      if (d.model && !this.ball3d && this.dataset.three && this.dataset.ball3d) {
        this.ball3d = import(this.dataset.ball3d)
          .then((m) => m.mount(fig, {three: this.dataset.three, pack: d.model}))
          .catch(() => null);
      }
      if (!this.ball3d) return;
      const want = Boolean(d.model);
      if (this.api3d !== undefined && this.shown3d === want) return;   // nothing changed: don't re-show (it would resize the 3D view)
      this.ball3d.then((api) => {
        this.api3d = api;
        const on = Boolean(api && this.design.model);
        fig.classList.toggle('is-3d', on);
        this.shown3d = on;
        if (!api) return;
        api.show(on);
        if (api.pause) api.pause(Boolean(this.typing));
        if (on) api.setName(this.shownName || '');
      });
    }

    // ---- phones: while a name is being typed (keyboard up) ----
    // The pinned preview shrinks (no size line), a mini row of the designs sits under it so a name's design can be
    // changed without closing the keyboard, the WhatsApp button steps aside, and the name stays just under the preview.
    initTyping() {
      this.narrow = window.matchMedia('(max-width: 989px)');
      const stage = this.querySelector('.orn__stage');
      if (stage && this.designs.length > 1) {
        const mini = document.createElement('div');
        mini.className = 'orn__mini';
        mini.setAttribute('role', 'radiogroup');
        this.designs.forEach((d, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'orn__mini-btn';
          b.setAttribute('role', 'radio');
          b.setAttribute('aria-label', d.title);
          b.innerHTML = `<img src="${d.art}" alt="" width="30" height="36" loading="lazy">`;
          b.addEventListener('pointerdown', (e) => e.preventDefault());   // keep the focus (and the keyboard) on the name
          b.addEventListener('click', () => this.pickDesign(i));
          mini.appendChild(b);
        });
        stage.appendChild(mini);
        this.els.mini = [...mini.children];
      }
      this.els.rows.addEventListener('focusin', (e) => {
        if (!e.target.matches('input') || !this.narrow.matches) return;
        this.setTyping(true);
        this.keepInView();   // every name moved to ("Add another name", Enter) comes up under the preview too
      });
      this.els.rows.addEventListener('focusout', () => setTimeout(() => { if (!this.els.rows.contains(document.activeElement)) this.setTyping(false); }, 150));
    }

    setTyping(on) {
      if (this.typing === on) return;
      this.typing = on;
      this.classList.toggle('is-typing', on);
      document.documentElement.classList.toggle('orn-typing', on);   // the snow and the fairy lights rest while typing
      if (this.api3d && this.api3d.pause) this.api3d.pause(on);       // and the 3D ball stops swaying
      if (!on) { clearTimeout(this.thumbsTimer); this.drawThumbs(this.rows()); }
      if (this.place) this.place();
      if (on) this.keepInView();
    }

    // the name being typed sits just under the pinned preview, above the keyboard (checked again once the keyboard has opened)
    keepInView() {
      clearTimeout(this.viewTimer);
      const go = () => {
        const row = this.active, stage = this.querySelector('.orn__stage');
        if (!this.typing || !row || !stage) return;
        const r = row.getBoundingClientRect(), s = stage.getBoundingClientRect();
        const vv = window.visualViewport, bottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
        if (r.top < s.bottom + 6 || r.bottom > bottom - 6) window.scrollBy(0, r.top - (s.bottom + 12));
      };
      requestAnimationFrame(go);
      this.viewTimer = setTimeout(go, 380);
    }

    // The picked design goes to the highlighted name (and to the names added after it).
    pickDesign(i) {
      if (!this.designs[i]) return;
      this.lastDesign = i;
      if (this.active) this.active.dataset.design = String(i);
      this.justAdded = false;
      this.update();
      // picked a design for a name that isn't written yet: carry straight on typing it
      const input = this.active?.querySelector('input');
      if (input && !input.value.trim()) input.focus({preventScroll: true});
    }

    // Picker, big preview and size follow the highlighted name's design.
    showDesign() {
      const i = Number(this.active?.dataset.design) || 0, d = this.designOf(this.active);
      this.design = d;
      this.els.designBtns.forEach((b, j) => { b.setAttribute('aria-checked', String(j === i)); b.tabIndex = j === i ? 0 : -1; });
      if (this.els.mini) this.els.mini.forEach((b, j) => b.setAttribute('aria-checked', String(j === i)));
      if (this.els.treeImg.getAttribute('src') !== d.art) this.els.treeImg.src = d.art;
      this.els.big.setAttribute('viewBox', d.viewbox);
      this.sync3d(d);
      if (this.els.sizeText) this.els.sizeText.textContent = this.t('size', '%w% × %h% cm', {w: d.wcm, h: d.hcm});
      if (this.els.designFor) {
        const v = this.active ? this.clean(this.active.querySelector('input').value) : '';
        this.els.designFor.textContent = v && ALLOWED.test(v) ? this.t('design_for', 'for %name%', {name: v}) : '';
      }
    }

    arrowKeys(e) {
      if (!e.target.matches('[data-design]') || !['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
      e.preventDefault();
      const n = this.els.designBtns.length, i = this.els.designBtns.indexOf(e.target);
      const next = (i + ((e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1) + n) % n;
      this.pickDesign(next); this.els.designBtns[next].focus();
    }

    update() {
      const list = this.rows();
      let bad = false;
      const names = [];
      const many = this.designs.length > 1;
      this.showDesign();
      list.forEach((row, i) => {
        const input = row.querySelector('input');
        const v = this.clean(input.value);
        const invalid = v && !ALLOWED.test(v);
        bad = bad || Boolean(invalid);
        row.classList.toggle('is-invalid', Boolean(invalid));
        row.classList.toggle('is-active', row === this.active);
        input.setAttribute('aria-invalid', String(Boolean(invalid)));
        const ph = i === 0 ? this.t('example', 'e.g. %name%', {name: this.examples[0] || 'Maria'}) : this.t('name_label', 'Name %n%', {n: i + 1});
        if (input.placeholder !== ph) input.placeholder = ph;
        const label = row.querySelector('[data-row-label]'), lt = this.t('name_label', 'Name %n%', {n: i + 1});
        if (label.textContent !== lt) label.textContent = lt;
        row.querySelector('[data-remove]').hidden = list.length === 1 && !v;
        const art = row.querySelector('[data-row-art]');
        if (art) {
          art.hidden = !many;
          const d = this.designOf(row);
          if (many && art.getAttribute('src') !== d.art) { art.src = d.art; art.alt = d.title; art.title = d.title; }
        }
        if (v && !invalid) names.push({name: v, design: this.designOf(row)});
      });
      this.names = names;
      this.els.more.hidden = list.length >= this.max;
      this.drawBig();
      // the "Your ornaments" thumbnails: right away, or once the typing pauses (rebuilding them on every key is slow on phones)
      clearTimeout(this.thumbsTimer);
      if (this.typing) this.thumbsTimer = setTimeout(() => this.drawThumbs(this.rows()), 220);
      else this.drawThumbs(list);

      const n = names.length;
      if (this.dataset.preorder === 'true') return this.updatePreorder(names, bad, many);
      const prices = names.map((x) => (Number(x.design.price) || 0) / 100);
      const one = (Number(this.design.price) || 0) / 100;
      const same = prices.every((x) => x === prices[0]);
      this.els.each.textContent = !n ? '' : same ? this.t('each_line', '%count% × %price%', {count: n, price: this.money(prices[0])})
        : this.t('count_line', '%count% ornaments', {count: n});
      this.els.total.textContent = this.money(Math.round((n ? prices.reduce((a, b) => a + b, 0) : one) * 100) / 100);
      let problem = '';
      if (bad) problem = this.t('row_error', 'Please write names in English letters (A–Z).');
      else if (names.some((x) => !x.design.variant) || (!n && !this.design.variant)) problem = this.t('err_setup', "This product isn't set up yet.");
      else if (names.some((x) => !x.design.available)) problem = this.t('soldout', 'Sold out for now.');
      if (this.els.add) this.els.add.disabled = Boolean(problem) || !n || this.busy;
      if (problem || !this.justAdded) this.els.msg.textContent = problem;
      this.els.msg.classList.toggle('is-ok', Boolean(this.justAdded && !problem));
    }

    // Pre-order (no prices yet): the button opens WhatsApp with every name and its design; nothing goes to the cart.
    updatePreorder(names, bad, many) {
      const n = names.length;
      const ok = n > 0 && !bad;
      const list = names.map((x) => `• ${x.name} (${x.design.title})`).join('\n');   // one design per page now: still say which
      if (this.els.pre) {
        this.els.pre.classList.toggle('is-disabled', !ok);
        this.els.pre.setAttribute('aria-disabled', String(!ok));
        this.els.pre.href = ok ? `https://wa.me/${this.dataset.whatsapp}?text=${encodeURIComponent(this.t('preorder_msg', "Hi Squigli! I'd like to pre-order: %names%", {names: '\n' + list, count: n}))}` : '#';
      }
      this.els.msg.textContent = bad ? this.t('row_error', 'Please write names in English letters (A–Z).') : '';
    }

    // Mini trees for every name once there are two or more; tapping one shows it big.
    drawThumbs(list) {
      const filled = list.filter((r) => { const v = this.clean(r.querySelector('input').value); return v && ALLOWED.test(v); });
      this.els.strip.hidden = filled.length < 2;
      this.els.thumbs.textContent = '';
      if (filled.length < 2) return;
      filled.forEach((row) => {
        const name = this.clean(row.querySelector('input').value);
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'orn__thumb' + (row === this.active ? ' is-on' : '');
        b.setAttribute('aria-label', name);
        b.setAttribute('aria-pressed', String(row === this.active));
        const d = this.designOf(row);
        const img = document.createElement('img');
        img.src = d.art || this.els.treeImg.src; img.alt = ''; img.width = 90; img.height = 112;
        const svg = document.createElementNS(SVGNS, 'svg');
        svg.setAttribute('viewBox', d.viewbox); svg.setAttribute('class', 'orn__name');
        drawName(svg, name, d);
        const label = document.createElement('span');
        label.className = 'orn__thumb-name'; label.textContent = name;
        b.append(img, svg, label);
        b.addEventListener('click', () => { this.active = row; this.update(); });
        this.els.thumbs.append(b);
      });
    }

    addToCart() {
      const names = this.names || [];
      if (!names.length || names.some((x) => !x.design.variant)) return;
      const items = names.map((x) => ({id: Number(x.design.variant), quantity: 1, properties: {Name: x.name}}));
      const drawer = document.querySelector('cart-drawer');
      const body = {items};
      if (drawer && typeof drawer.renderContents === 'function') {
        body.sections = drawer.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      }
      this.busy = true; this.justAdded = false; this.update();
      fetch('/cart/add.js', {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(body)})
        .then((r) => r.json())
        .then((res) => {
          if (res.status) { this.els.msg.textContent = res.description || this.t('could_not_add', 'Could not add to cart.'); return; }
          if (drawer && res.sections) { drawer.classList.remove('is-empty'); drawer.renderContents(res); }
          else { window.location.href = '/cart'; return; }
          // fresh start for the next batch
          this.rows().forEach((r, i) => { if (i) r.remove(); });
          this.rows()[0].querySelector('input').value = '';
          this.active = this.rows()[0];
          this.justAdded = true;
          this.els.msg.textContent = this.t('added', 'Added to your cart! Write another name to keep going.');
        })
        .catch(() => { this.els.msg.textContent = this.t('could_not_add', 'Could not add to cart.'); })
        .finally(() => { this.busy = false; this.update(); });
    }
  }

  customElements.define('squigli-ornament', SquigliOrnament);
})();
